import { useState } from 'react';

import useAddableMembers from '../../../../../hooks/api/teams/useAddableMembers';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { InfiniteSelect } from '../../../../generic/Select/InfiniteSelect';
import { type AddableUser, AddUserOption } from '../management/AddUserOption';

type Props = {
  id: string;
  /**
   * The team (or team group) to list the addable users of.
   */
  teamId: number;
  value: AddableUser[];
  onChange: (users: AddableUser[]) => void;
  /**
   * Pass false to hold off fetching, eg. while the modal around the select is hidden.
   */
  shouldFetch?: boolean;
};

/**
 * A multi-select of the users that can still be added to a team. Users are loaded 20 at a time as the end of the
 * menu is reached, and typing searches on the server.
 */
export const AddableMembersSelect = ({ id, teamId, value, onChange, shouldFetch }: Props) => {
  const [search, setSearch] = useState('');
  const { debouncedValue: debouncedSearch } = useDebouncedValue(search, 200);
  const query = useAddableMembers(teamId, 20, debouncedSearch, shouldFetch);

  const options = query.data?.pages.flatMap((page) => page.results.map((user) => ({
    label: user.username || user.email || `user id: ${user.id}`,
    value: user.id,
    isServiceAccount: user.isServiceAccount,
  }))) ?? [];

  return (
    <InfiniteSelect
      id={id}
      isMulti
      options={options}
      value={value}
      onChange={onChange}
      onInputChange={setSearch}
      query={query}
      components={{ Option: AddUserOption }}
    />
  );
};
